/**
 * Pure predicates for the hospitality venue-home real-estate additions
 * (Issy's Love Inn rework 2026-09-07). Extracted from `web/src/pages/venue.tsx`
 * so the visibility gates are unit-testable in isolation, matching the
 * router-decisions.ts pattern that closed the PR #184 gap.
 *
 * Rules encoded here:
 *   - Stats block is member-only, non-studio, requires a signed-in user.
 *   - Loyalty card only if the venue has loyalty turned on; when off the
 *     ticket card expands to full width.
 *   - Latest news renders whenever a bulletin exists AND the venue isn't a
 *     studio (studios use their own layout).
 *   - "This week" = events starting in the next 7 days; falls back to the
 *     first N upcoming when nothing lands in the week so the section never
 *     collapses to empty on a quiet week.
 */

type HomeEvent = { id: string; date: string | Date };

export function shouldShowStatsBlock(input: {
  hasActiveAccess: boolean;
  isStudio: boolean;
  hasUser: boolean;
}): boolean {
  return input.hasActiveAccess && !input.isStudio && input.hasUser;
}

export function shouldShowLoyaltyCard(input: {
  statsVisible: boolean;
  loyaltyEnabled: boolean;
}): boolean {
  return input.statsVisible && input.loyaltyEnabled;
}

export function shouldShowLatestNews(input: {
  isStudio: boolean;
  hasBulletin: boolean;
  /** Signed-in gate. Jair @ Love Inn 2026-09-30: bulletins should
   *  live behind the signup wall so members-only content (ticket
   *  drops, drink drops) is the price of admission. */
  hasUser: boolean;
}): boolean {
  return !input.isStudio && input.hasBulletin && input.hasUser;
}

// "Coming soon" teaser card on the venue home (Issy 2026-09-09 Love Inn
// polish). Cross-tenant peer-review fix 2026-09-09: was hardcoded on
// every non-studio venue, which surfaced Love Inn's product roadmap to
// Prospect/Toka/etc. Now gated on venue-configurable appConfig, so it
// only renders where a venue has explicitly opted in via
// appConfig.homeComingSoon.enabled = true. Copy is also configurable
// (title, body) with a benign default for the opt-in case.
type HomeComingSoonConfig = {
  enabled?: boolean;
  title?: string;
  body?: string;
};

export function shouldShowComingSoon(input: {
  isStudio: boolean;
  config: HomeComingSoonConfig | null | undefined;
}): boolean {
  return !input.isStudio && input.config?.enabled === true;
}

export function pickThisWeekEvents<E extends HomeEvent>(input: {
  events: E[];
  now: Date;
  fallbackCount?: number;
}): { list: E[]; hasMore: boolean } {
  const fallback = input.fallbackCount ?? 4;
  // "This week" = today through the coming Sunday (UK convention: weeks
  // run Mon-Sun). Was previously "next 7 rolling days" which spilled into
  // next week whenever `now` was mid-week, Issy at Love Inn on Thu 17 Sep
  // saw the following Mon-Wed listed as "This week" (2026-09-17). The
  // existing tests used a Monday `now` so both interpretations gave the
  // same result and the bug was invisible.
  const endOfWeek = endOfCurrentWeek(input.now);
  const upcoming = input.events.filter((e) => new Date(e.date) >= input.now);
  const thisWeek = upcoming.filter((e) => new Date(e.date) <= endOfWeek);
  const list = thisWeek.length > 0 ? thisWeek : upcoming.slice(0, fallback);
  const hasMore = upcoming.length > list.length;
  return { list, hasMore };
}

/**
 * End of the current calendar week (Sunday 23:59:59.999 local), given any
 * moment inside the week. Sunday returns itself (Sunday is the LAST day of
 * the UK week; today's events still count). Pure, no I/O.
 *
 * Timezone: uses `setHours`, which is LOCAL time. `startOfNextWeek` +
 * `endOfNextWeek` layer on top and inherit that, so all three functions
 * agree with the user's clock (UK for our venues, matches Issy's
 * "this week" expectation). The 1ms increment in startOfNextWeek keeps
 * the two windows disjoint even across a DST transition.
 */
export function endOfCurrentWeek(now: Date): Date {
  const end = new Date(now.getTime());
  const dow = end.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
  const daysUntilSunday = dow === 0 ? 0 : 7 - dow;
  end.setDate(end.getDate() + daysUntilSunday);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Start of the NEXT calendar week (Monday 00:00:00.000 local): the moment
 * immediately after `endOfCurrentWeek(now)`. Used to bracket the "Next
 * week" section on the venue home.
 */
export function startOfNextWeek(now: Date): Date {
  const start = endOfCurrentWeek(now);
  start.setMilliseconds(start.getMilliseconds() + 1);
  return start;
}

/** End of the NEXT calendar week (following Sunday 23:59:59.999 local). */
export function endOfNextWeek(now: Date): Date {
  const end = endOfCurrentWeek(now);
  end.setDate(end.getDate() + 7);
  return end;
}

/**
 * "Next week" = events in the following calendar week (Mon 00:00 → Sun
 * 23:59). No fallback: an empty next-week returns an empty list, so the
 * caller can hide the section entirely rather than duplicate a fallback
 * list that "This week" is already showing.
 *
 * Fenton 2026-09-20: Love Inn app should show a second section under
 * "This week" so members scrolling the home surface see what's coming
 * without leaving the app.
 */
export function pickNextWeekEvents<E extends HomeEvent>(input: {
  events: E[];
  now: Date;
}): { list: E[] } {
  const start = startOfNextWeek(input.now);
  const end = endOfNextWeek(input.now);
  const list = input.events.filter((e) => {
    const d = new Date(e.date);
    return d >= start && d <= end;
  });
  return { list };
}

/**
 * Return up to two upcoming-week sections for the venue home, labelled by
 * their relation to today. Fixes the "Sunday-evening confusion" case
 * (Fenton 2026-09-20): my initial rollout showed "This week" on a Sunday
 * evening when the current calendar week was already spent, and the list
 * fell back to next-week's events — so users saw "This week" over what
 * was really next week. Now the labels track what's actually inside.
 *
 * Algorithm: scan up to 4 calendar weeks starting from the current one,
 * skip weeks with zero upcoming events, take the first two non-empty
 * weeks. Each section carries the calendar-week span it represents so
 * the caller can render dates alongside the label if it wants.
 *
 * Labels:
 *   - offset 0 (current calendar week, contains today):  "This week"
 *   - offset 1 (next calendar week):                     "Next week"
 *   - offset 2 (week after next):                        "The week after"
 *   - offset 3+ (rare, big quiet gap):                   "Week of DD MMM"
 *
 * Only future events count (events.filter e.date >= now inside the loop),
 * so a section for the current week on a Sunday evening naturally comes
 * back empty and gets skipped, and the caller sees "Next week" +
 * "The week after" instead of a stale "This week" showing next-week's rows.
 */
export type HomeWeekSection<E> = {
  label: string;
  offset: number; // 0 = current calendar week, 1 = next, 2 = week after, …
  weekStart: Date; // Mon 00:00 local of the section's week
  weekEnd: Date; // Sun 23:59:59.999 local
  events: E[];
};

export function pickHomeWeekSections<E extends HomeEvent>(input: {
  events: E[];
  now: Date;
  maxWeeksScanned?: number;
  maxSections?: number;
}): HomeWeekSection<E>[] {
  const maxWeeksScanned = input.maxWeeksScanned ?? 4;
  const maxSections = input.maxSections ?? 2;
  const upcoming = input.events.filter((e) => new Date(e.date) >= input.now);
  const sections: HomeWeekSection<E>[] = [];

  for (let offset = 0; offset < maxWeeksScanned && sections.length < maxSections; offset++) {
    const { start, end } = weekBoundsAtOffset(input.now, offset);
    const events = upcoming.filter((e) => {
      const d = new Date(e.date);
      return d >= start && d <= end;
    });
    if (events.length === 0) continue;
    sections.push({
      label: labelForOffset(offset, start),
      offset,
      weekStart: start,
      weekEnd: end,
      events,
    });
  }

  return sections;
}

function weekBoundsAtOffset(now: Date, offset: number): { start: Date; end: Date } {
  if (offset === 0) {
    // Current calendar week: Mon 00:00 → Sun 23:59 of the week that
    // contains `now`. We return the full calendar-week span (not `now`
    // → Sun) because the caller has already filtered to future events
    // via `upcoming = events.filter(e => e.date >= now)` — so a past
    // event earlier today can never leak in, and the calendar-week
    // start keeps the span consistent with offsets 1, 2, 3.
    return { start: startOfCurrentWeek(now), end: endOfCurrentWeek(now) };
  }
  // Offsets 1, 2, 3, … each start at the previous week's start + offset*7 days.
  const currStart = startOfCurrentWeek(now);
  const start = new Date(currStart.getTime());
  start.setDate(start.getDate() + offset * 7);
  const end = new Date(start.getTime());
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function startOfCurrentWeek(now: Date): Date {
  const d = new Date(now.getTime());
  const dow = d.getDay(); // 0 Sun … 6 Sat
  const daysSinceMonday = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - daysSinceMonday);
  d.setHours(0, 0, 0, 0);
  return d;
}

function labelForOffset(offset: number, weekStart: Date): string {
  if (offset === 0) return "This week";
  if (offset === 1) return "Next week";
  if (offset === 2) return "The week after";
  return `Week of ${weekStart.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
}

// Redemption eligibility: strict ACTIVE only. Deliberately NARROWER than
// `hasActiveAccess` (which includes CANCELED-in-period for read-side venue
// access), because the redeem API rejects anything but ACTIVE and we don't
// want the featured perk card to render a button that will 403. Peer review
// 2026-09-09 caught the mismatch (dc077dcb).
export function isRedemptionEligible(status: string | null | undefined): boolean {
  return status === "ACTIVE";
}

// Redeemables surfaced on the venue home for members. Selection rules,
// same as pickFeaturedPerk but returns the full list (Fenton 2026-09-09,
// "redeem from the main UI not a click-through"):
//   1. Only benefits on tiers the user's tier can access (tier.sortOrder
//      <= their tier sortOrder), so a Community member never sees a
//      Regulars-only perk dangled at them.
//   2. Only benefits with isRedeemable: true (passive benefits like "10%
//      off tickets" have no home surface).
//   3. Welcome rewards first (one-off perks that decay in value the
//      longer they're unclaimed), then everything else.
//   4. Deterministic tiebreak on id so re-renders don't reorder.
type PerkBenefit = {
  id: string;
  isRedeemable?: boolean;
  isWelcomeReward?: boolean;
  description?: string | null;
  tier: { sortOrder: number };
};

export function pickHomeRedeemables<B extends PerkBenefit>(input: {
  benefits: B[];
  memberTierSortOrder: number | null;
}): B[] {
  if (input.memberTierSortOrder == null) return [];
  const eligible = input.benefits.filter(
    (b) => b.isRedeemable === true && b.tier.sortOrder <= input.memberTierSortOrder!,
  );
  return [...eligible].sort((a, b) => {
    const aw = a.isWelcomeReward ? 0 : 1;
    const bw = b.isWelcomeReward ? 0 : 1;
    if (aw !== bw) return aw - bw;
    return a.id.localeCompare(b.id);
  });
}
